import { checkAppVersion } from 'components/CheckAppVersion/checkAppVersion'
import { ComponentType, lazy, LazyExoticComponent } from 'react'
import type { NavigateFunction } from 'react-router'
import { TelemetryService } from 'services/Telemetry'

/**
 * Drop-in replacement for React.lazy that survives transient chunk load
 * failures (CDN blips, truncated responses, flaky connections).
 *
 * Why this is not just "call the factory again":
 * a failed module fetch is recorded in the browser's module map, so re-running
 * the same `import('./x')` resolves to the cached rejection without touching
 * the network. To force a real refetch we pull the chunk URL out of the error
 * message and import it again under a cache-busting query string, which is a
 * distinct module map entry.
 *
 * Only the chunk itself is re-executed; its own (already resolved) imports keep
 * pointing at their canonical URLs.
 *
 * @example
 * const Page = lazyWithRetry(() => import('./Page'))
 * const Tab = lazyWithRetry(() => import('./Tabs'), 'FinancialTab')
 */

export interface LazyWithRetryOptions {
  /** Extra attempts after the first one fails. Defaults to 3. */
  retries?: number
  /** Backoff before the first retry, doubled each attempt. Defaults to 300ms. */
  initialDelayMs?: number
}

const DEFAULT_RETRIES = 3
const DEFAULT_INITIAL_DELAY_MS = 300

/**
 * Chrome/Edge: "Failed to fetch dynamically imported module: <url>"
 * Firefox:     "error loading dynamically imported module: <url>"
 * Safari reports "Importing a module script failed." with no URL, in which case
 * we fall back to plain factory retries.
 */
const chunkUrlPattern =
  /(?:failed to fetch|error loading) dynamically imported module:?\s*(https?:\/\/\S+)/i

export const getFailedChunkUrl = (error: unknown): string | undefined => {
  const message = error instanceof Error ? error.message : String(error ?? '')
  return chunkUrlPattern.exec(message)?.[1]
}

export const withCacheBuster = (url: string, attempt: number): string => {
  const parsed = new URL(url, window.location.href)
  parsed.searchParams.set('retry', `${attempt}-${Date.now()}`)
  return parsed.href
}

const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * checkAppVersion only uses `navigate` for the incognito sessionStorage case,
 * which does not apply here.
 */
const noopNavigate = (() => undefined) as unknown as NavigateFunction

/**
 * A chunk we cannot load after several attempts usually means this tab is
 * running an index.html from an older build. Ask the version endpoint: if a
 * newer build is live, checkAppVersion clears the caches and reloads for us.
 *
 * @returns true when a reload is already under way
 */
const isReloadingToNewerVersion = async (): Promise<boolean> => {
  try {
    const version = await checkAppVersion(noopNavigate)
    return !version.isLatest
  } catch (error) {
    console.error('rh24 unable to check app version after chunk failure', error)
    return false
  }
}

const pickExport = <T>(
  module: Record<string, unknown>,
  exportName: string | undefined,
): { default: T } => {
  const key = exportName ?? 'default'
  const component = module?.[key]

  if (!component) {
    throw new Error(
      `lazyWithRetry: module loaded but has no "${key}" export (found: ${Object.keys(
        module ?? {},
      ).join(', ')})`,
    )
  }

  return { default: component as T }
}

const loadWithRetry = async <T>(
  factory: () => Promise<Record<string, unknown>>,
  exportName: string | undefined,
  options: LazyWithRetryOptions | undefined,
): Promise<{ default: T }> => {
  const retries = options?.retries ?? DEFAULT_RETRIES
  const initialDelayMs = options?.initialDelayMs ?? DEFAULT_INITIAL_DELAY_MS

  let lastError: unknown
  let failedUrl: string | undefined

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const module =
        attempt === 0 || !failedUrl
          ? await factory()
          : await import(/* @vite-ignore */ withCacheBuster(failedUrl, attempt))

      if (attempt > 0) {
        TelemetryService.getInstance().TrackEvent('chunkLoadRecovered', {
          chunkUrl: failedUrl ?? 'unknown',
          attempts: attempt,
        })
      }

      return pickExport<T>(module, exportName)
    } catch (error) {
      lastError = error
      failedUrl = failedUrl ?? getFailedChunkUrl(error)

      if (attempt === retries) break

      console.warn(
        `rh24 chunk load failed (attempt ${attempt + 1}/${retries + 1})`,
        failedUrl ?? '<unknown chunk>',
        error,
      )

      await delay(initialDelayMs * 2 ** attempt)
    }
  }

  TelemetryService.getInstance().logError(lastError)

  if (await isReloadingToNewerVersion()) {
    // Keep the Suspense fallback on screen while the reload happens, rather
    // than flashing the error boundary at a user who is about to get a fresh
    // page anyway.
    return new Promise<never>(() => undefined)
  }

  throw lastError
}

/**
 * Lazy load a module's default export, retrying transient network failures.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  exportName?: undefined,
  options?: LazyWithRetryOptions,
): LazyExoticComponent<T>
/**
 * Lazy load a named export from a module, retrying transient network failures.
 */
export function lazyWithRetry<
  TModule extends Record<string, any>,
  TKey extends keyof TModule & string,
>(
  factory: () => Promise<TModule>,
  exportName: TKey,
  options?: LazyWithRetryOptions,
): LazyExoticComponent<
  TModule[TKey] extends ComponentType<any> ? TModule[TKey] : never
>
export function lazyWithRetry(
  factory: () => Promise<Record<string, unknown>>,
  exportName?: string,
  options?: LazyWithRetryOptions,
): LazyExoticComponent<ComponentType<any>> {
  return lazy(() =>
    loadWithRetry<ComponentType<any>>(factory, exportName, options),
  )
}
